import React, { useState, useEffect, useMemo } from 'react';
import { locationService, Country } from '../services/locations';
import { Pagination } from '../components/Pagination';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Loader2, Globe, Edit, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';
import { useForm } from 'react-hook-form';

interface CountryFormData {
  name: string;
  code: string;
}

export const CountriesPage: React.FC = () => {
  const [allCountries, setAllCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCountry, setEditingCountry] = useState<Country | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Client-side pagination
  const totalElements = allCountries.length;
  const totalPages = Math.ceil(totalElements / pageSize);
  const countries = useMemo(() => {
    const start = currentPage * pageSize;
    return allCountries.slice(start, start + pageSize);
  }, [allCountries, currentPage, pageSize]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CountryFormData>();

  useEffect(() => {
    loadCountries();
  }, []);

  const loadCountries = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await locationService.getAllCountries();
      setAllCountries(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load countries');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (country?: Country) => {
    if (country) {
      setEditingCountry(country);
      reset({
        name: country.name,
        code: country.code,
      });
    } else {
      setEditingCountry(null);
      reset({});
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCountry(null);
    reset({});
  };

  const onSubmit = async (data: CountryFormData) => {
    try {
      if (editingCountry) {
        await locationService.updateCountry(editingCountry.id, data);
      } else {
        await locationService.createCountry(data);
      }
      await loadCountries();
      handleCloseDialog();
    } catch (err: any) {
      setError(err.message || 'Failed to save country');
    }
  };

  const handleDelete = async (id: number) => {
    if (
      window.confirm(
        'Are you sure you want to delete this country? This will also delete all associated cities.'
      )
    ) {
      try {
        await locationService.deleteCountry(id);
        await loadCountries();
      } catch (err: any) {
        setError(err.message || 'Failed to delete country');
      }
    }
  };

  if (loading && countries.length === 0) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Countries</h1>
          <p className="text-muted-foreground">Manage countries for location data</p>
        </div>
        <Button onClick={() => handleOpenDialog()}>
          <Plus className="h-4 w-4 mr-2" />
          Add Country
        </Button>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
          <p className="font-medium">Error</p>
          <p className="text-sm">{error}</p>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>All Countries</CardTitle>
          <CardDescription>List of all countries in the system</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {countries.map((country) => (
                <TableRow key={country.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Globe className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{country.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{country.code}</TableCell>
                  <TableCell>
                    <Badge variant={country.active ? 'default' : 'secondary'}>
                      {country.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(country)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(country.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {countries.length === 0 && (
            <p className="text-center text-sm text-muted-foreground py-8">No countries found</p>
          )}

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalElements={totalElements}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(0);
            }}
            itemName="countries"
          />
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCountry ? 'Edit Country' : 'Create Country'}</DialogTitle>
            <DialogDescription>
              {editingCountry ? 'Update country details' : 'Add a new country'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <Label htmlFor="name">Country Name *</Label>
              <Input
                id="name"
                {...register('name', { required: true })}
                placeholder="e.g., Turkey, United States"
              />
              {errors.name && <p className="text-sm text-destructive">Name is required</p>}
            </div>

            <div>
              <Label htmlFor="code">Country Code *</Label>
              <Input
                id="code"
                {...register('code', { required: true, maxLength: 3 })}
                placeholder="e.g., TR, US"
              />
              {errors.code && (
                <p className="text-sm text-destructive">Code is required (max 3 chars)</p>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button type="submit">{editingCountry ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CountriesPage;
